import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MessageSquare } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { createShareComment, listShareComments, shareChatKeys } from '../share/shareApi';

/**
 * Comments for a public share link with `allowChat` enabled — list + reply
 * only, same reduced surface as `SharedCommentsPanel` (no edit/resolve/pin/
 * @mentions, which require org membership on the backend).
 */
export default function PublicShareCommentsPanel({ token, password }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: shareChatKeys.comments(token),
    queryFn: () => listShareComments({ token, password }),
    enabled: Boolean(token),
  });
  const roots = data?.data ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: shareChatKeys.comments(token) });

  const createMutation = useMutation({
    mutationFn: createShareComment,
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not comment')),
  });

  const submit = () => {
    if (!body.trim()) return;
    createMutation.mutate({ token, body, password });
    setBody('');
  };

  const submitReply = (parentCommentId) => {
    if (!replyBody.trim()) return;
    createMutation.mutate({ token, body: replyBody, parentCommentId, password });
    setReplyBody('');
    setReplyTo(null);
  };

  return (
    <div className="flex min-h-0 flex-col gap-3 rounded-lg border border-slate-200 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Comments ({data?.meta?.total ?? roots.length})
      </p>

      <div className="space-y-2">
        <textarea
          rows={2}
          value={body}
          placeholder="Add a comment…"
          onChange={(e) => setBody(e.target.value)}
          className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button size="sm" className="ml-auto" onClick={submit} loading={createMutation.isPending}>
          Comment
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : roots.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No comments yet.</p>
      ) : (
        <ul className="scrollbar-thin max-h-80 min-h-0 space-y-3 overflow-auto">
          {roots.map((c) => (
            <li key={c.id} className="rounded-lg border border-slate-100 p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">
                  {c.author?.displayName ?? 'Unknown'}
                </span>
                <span className="text-xs text-slate-400">{formatDate(c.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-600">{c.body}</p>
              <button
                className="mt-2 text-xs text-slate-400 hover:text-brand-600"
                onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
              >
                <MessageSquare className="mr-1 inline h-3 w-3" />
                {c.replyCount} {c.replyCount === 1 ? 'reply' : 'replies'}
              </button>
              {replyTo === c.id && (
                <div className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3">
                  <textarea
                    rows={2}
                    value={replyBody}
                    placeholder="Reply…"
                    onChange={(e) => setReplyBody(e.target.value)}
                    className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <Button size="sm" onClick={() => submitReply(c.id)} loading={createMutation.isPending}>
                    Reply
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
